import { CalendarBlankIcon } from '@phosphor-icons/react';
import { format } from 'date-fns/format';
import { enUS } from 'date-fns/locale';
import { useState } from 'react';
import { Control, FieldValues, Path } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useGetTestId } from '@/hooks';
import { cn } from '@/lib/utils';

type DatePickerProps<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  dateFormat?: string;
  requiredMark?: boolean;
};

const customLocale = {
  ...enUS,
};

export const DatePicker = <T extends FieldValues>({
  name,
  control,
  label = 'Select date',
  dateFormat = 'yyyy-MM-dd',
  requiredMark,
}: DatePickerProps<T>) => {
  const testId = useGetTestId(name);
  const [open, setOpen] = useState(false);
  const disabledDays = (date: Date) => date < new Date(); // disabling dates before today

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-col gap-1">
          {label && (
            <FormLabel htmlFor={name} className="text-sm font-medium">
              {label}
              {requiredMark && <span className="text-red-500">*</span>}
            </FormLabel>
          )}
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  id={name}
                  data-testid={testId}
                  aria-label={`Select ${label.toLowerCase()}`}
                  variant="outline"
                  className={cn(
                    'w-full h-11 pl-3 text-left font-normal',
                    !field.value && 'text-muted-foreground'
                  )}
                >
                  {field.value ? (
                    format(new Date(field.value), dateFormat)
                  ) : (
                    <span>YYYY-MM-DD</span>
                  )}
                  <CalendarBlankIcon className="ml-auto h-4 w-4 opacity-50" />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={new Date(field.value)}
                onSelect={(date) => {
                  field.onChange(format(date, dateFormat));
                  setOpen(false);
                }}
                disabled={disabledDays}
                initialFocus
                locale={customLocale}
                defaultMonth={field.value}
              />
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
